import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
//
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '@/components/Iconify';
import FormProvider from '@/components/hook-form/FormProvider';
import RHFTextField from '@/components/hook-form/RHFTextField';
import { DashboardDuoChallengeResult } from '@/features/app';
// hooks + utils
import useAuth from '@/hooks/useAuth';
import useIsMountedRef from '@/hooks/useIsMountedRef';
import { authPath } from '../routes/paths';
import { LOGIN_FORM_SCHEMA, LOGIN_FORM_DEFAULT_VALUES } from '../utils';
// types
import { LoginFormValuesProps } from '../types';
// ----------------------------------------------------------------------

const DUO_POLL_INTERVAL_MS = 2000;
const MAX_EMAIL_FALLBACK_ATTEMPTS = 5;
const DEFAULT_EMAIL_RESEND_COOLDOWN_S = 30;

type EmailFallbackStatus = 'idle' | 'sending' | 'sent' | 'verifying';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getErrorMessage = (
  error: unknown,
  fallback = 'Something went wrong'
): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (!isRecord(error)) {
    return fallback;
  }

  const message = error.message;
  if (typeof message === 'string') {
    return message;
  }

  const nestedError = error.error;
  if (isRecord(nestedError) && typeof nestedError.message === 'string') {
    return nestedError.message;
  }

  return fallback;
};

const getErrorStatus = (error: unknown): number | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }

  const status = error.status;
  return typeof status === 'number' ? status : undefined;
};

const parseCooldownSeconds = (message: string): number => {
  const match = message.match(/(\d+)\s*s\b/i);
  if (match) {
    const seconds = Number(match[1]);
    if (Number.isFinite(seconds) && seconds > 0) {
      return seconds;
    }
  }

  return DEFAULT_EMAIL_RESEND_COOLDOWN_S;
};

export default function LoginForm() {
  const {
    login,
    pollDashboardDuoChallenge,
    requestDuoEmailFallback,
    verifyDuoEmailFallbackCode,
  } = useAuth();
  const isMountedRef = useIsMountedRef();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [duoChallenge, setDuoChallenge] =
    useState<DashboardDuoChallengeResult | null>(null);
  const [emailFallbackStatus, setEmailFallbackStatus] =
    useState<EmailFallbackStatus>('idle');
  const [emailFallbackMaskedEmail, setEmailFallbackMaskedEmail] = useState<
    string | null
  >(null);
  const [emailFallbackCode, setEmailFallbackCode] = useState('');
  const [emailFallbackError, setEmailFallbackError] = useState<string | null>(
    null
  );
  const [emailFallbackWrongAttempts, setEmailFallbackWrongAttempts] =
    useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const duoPollTimerRef = useRef<number | null>(null);
  const duoChallengeIdRef = useRef<string | null>(null);
  const duoPollingCancelledRef = useRef(true);
  const scheduleDuoPollRef = useRef<(challengeId: string) => void>(
    () => undefined
  );
  const locationKey = `${location.pathname}${location.search}${location.hash}`;
  const previousLocationKeyRef = useRef(locationKey);

  const handleToggleShowPassword = () => setShowPassword(!showPassword);

  const methods = useForm<LoginFormValuesProps>({
    resolver: yupResolver(LOGIN_FORM_SCHEMA),
    defaultValues: LOGIN_FORM_DEFAULT_VALUES,
  });

  const {
    reset: resetFormValues,
    setError,
    clearErrors,
    getValues,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const clearDuoPolling = useCallback(() => {
    duoPollingCancelledRef.current = true;
    duoChallengeIdRef.current = null;

    if (duoPollTimerRef.current !== null) {
      window.clearTimeout(duoPollTimerRef.current);
      duoPollTimerRef.current = null;
    }
}, []);

  const resetEmailFallback = useCallback(() => {
    setEmailFallbackStatus('idle');
    setEmailFallbackMaskedEmail(null);
    setEmailFallbackCode('');
    setEmailFallbackError(null);
    setEmailFallbackWrongAttempts(0);
    setResendCooldown(0);
  }, []);

  const returnToLoginWithError = useCallback(
    (message: string) => {
      const email = getValues('email');

      clearDuoPolling();
      setDuoChallenge(null);
      resetEmailFallback();
      resetFormValues({ ...LOGIN_FORM_DEFAULT_VALUES, email, password: '' });
      setError('afterSubmit', { type: 'duo', message });
    },
    [clearDuoPolling, getValues, resetEmailFallback, resetFormValues, setError]
  );

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendCooldown > 0]);

  useEffect(() => {
    scheduleDuoPollRef.current = (challengeId: string) => {
      if (duoPollTimerRef.current) {
        window.clearTimeout(duoPollTimerRef.current);
      }

      if (duoPollTimerRef.current !== null) {
        window.clearTimeout(duoPollTimerRef.current);
      }

      duoPollTimerRef.current = window.setTimeout(async () => {
        duoPollTimerRef.current = null;

        if (
          duoPollingCancelledRef.current ||
          duoChallengeIdRef.current !== challengeId
        ) {
          return;
        }

        try {
          const result = await pollDashboardDuoChallenge(challengeId);

          if (
            duoPollingCancelledRef.current ||
            duoChallengeIdRef.current !== challengeId
          ) {
            return;
          }

          if (result.mfa_required) {
            setDuoChallenge(result);
            scheduleDuoPollRef.current(challengeId);
            return;
          }

          clearDuoPolling();
        } catch (error) {
          if (
            duoPollingCancelledRef.current ||
            duoChallengeIdRef.current !== challengeId
          ) {
            return;
          }

          returnToLoginWithError(
            getErrorMessage(error, 'Duo verification failed.')
          );
        }
      }, DUO_POLL_INTERVAL_MS);
    };
  }, [clearDuoPolling, pollDashboardDuoChallenge, returnToLoginWithError]);

  useEffect(
    () => () => {
      clearDuoPolling();
    },
    [clearDuoPolling]
  );

  useEffect(() => {
    if (previousLocationKeyRef.current === locationKey) {
      return;
    }

    previousLocationKeyRef.current = locationKey;
    clearDuoPolling();
    setDuoChallenge(null);
    resetEmailFallback();
  }, [clearDuoPolling, locationKey, resetEmailFallback]);

  const startDuoChallenge = (challenge: DashboardDuoChallengeResult) => {
    clearDuoPolling();
    clearErrors('afterSubmit');
    resetEmailFallback();
    duoPollingCancelledRef.current = false;
    duoChallengeIdRef.current = challenge.challenge_id;
    setDuoChallenge(challenge);
    scheduleDuoPollRef.current(challenge.challenge_id);
  };

  const handleCancelDuo = () => {
    returnToLoginWithError('Duo verification cancelled. Please log in again.');
  };

  const handleRequestEmailFallback = async () => {
    if (!duoChallenge) {
      return;
    }

    setEmailFallbackStatus('sending');
    setEmailFallbackError(null);

    try {
      const result = await requestDuoEmailFallback(duoChallenge.challenge_id);

      if (!isMountedRef.current) {
        return;
      }

      setEmailFallbackMaskedEmail(result.email);
      setEmailFallbackWrongAttempts(0);
      setEmailFallbackStatus('sent');
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }

      const status = getErrorStatus(error);
      const message = getErrorMessage(error, 'Unable to send code.');

      if (status === 404) {
        returnToLoginWithError(
          'Your login session expired. Please log in again.'
        );
        return;
      }

      if (status === 429) {
        setResendCooldown(parseCooldownSeconds(message));
      }

      setEmailFallbackStatus(emailFallbackMaskedEmail ? 'sent' : 'idle');
      setEmailFallbackError(message);
    }
  };

  const handleVerifyEmailFallbackCode = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (!duoChallenge || emailFallbackCode.length !== 6) {
      return;
    }

    setEmailFallbackStatus('verifying');
    setEmailFallbackError(null);

    try {
      await verifyDuoEmailFallbackCode(
        duoChallenge.challenge_id,
        emailFallbackCode
      );
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }

      const status = getErrorStatus(error);
      const message = getErrorMessage(error, 'Invalid code.');

      if (status === 404) {
        returnToLoginWithError(
          'Your login session expired. Please log in again.'
        );
        return;
      }

      if (status === 401) {
        const nextAttempts = emailFallbackWrongAttempts + 1;
        setEmailFallbackWrongAttempts(nextAttempts);

        if (nextAttempts >= MAX_EMAIL_FALLBACK_ATTEMPTS) {
          returnToLoginWithError(
            'Too many incorrect attempts. Please log in again.'
          );
          return;
        }
      }

      setEmailFallbackStatus('sent');
      setEmailFallbackError(message);
    }
  };

  const onSubmitLogin = async (data: LoginFormValuesProps) => {
    try {
      const result = await login(data.email, data.password);

      if (result.mfa_required) {
        startDuoChallenge(result);
      }
    } catch (error) {
      resetFormValues({
        ...LOGIN_FORM_DEFAULT_VALUES,
        email: data.email,
        password: '',
      });

      if (isMountedRef.current) {
        setError('afterSubmit', {
          type: 'server',
          message: getErrorMessage(error),
        });
      }
    }
  };

  if (duoChallenge) {
    const showEmailFallback =
      (duoChallenge.status === 'unavailable' &&
        duoChallenge.fallback_available) ||
      emailFallbackStatus !== 'idle';

    return (
      <Stack spacing={2}>
        <Alert severity="info">
          {duoChallenge.status_msg ||
            'Pushed a login request to your phone.'}
        </Alert>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 1,
          }}
        >
          <CircularProgress size={28} />
          <Box>
            <Typography variant="subtitle1">
              Duo Mobile verification
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Waiting for approval...
            </Typography>
          </Box>
        </Box>

        {showEmailFallback && (
          <Stack
            spacing={1.5}
            component="form"
            onSubmit={handleVerifyEmailFallbackCode}
          >
            <Alert severity={emailFallbackMaskedEmail ? 'success' : 'warning'}>
              {emailFallbackMaskedEmail
                ? `Code sent to ${emailFallbackMaskedEmail}`
                : 'Having trouble? We can email you a code instead.'}
            </Alert>

            {emailFallbackError && (
              <Alert severity="error">{emailFallbackError}</Alert>
            )}

            {emailFallbackMaskedEmail && (
              <OutlinedInput
                value={emailFallbackCode}
                onChange={(event) =>
                  setEmailFallbackCode(
                    event.target.value.replace(/\D/g, '').slice(0, 6)
                  )
                }
                placeholder="6-digit code"
                autoFocus
                inputProps={{
                  maxLength: 6,
                  inputMode: 'numeric',
                  'aria-label': 'Email verification code',
                }}
              />
            )}

            {emailFallbackMaskedEmail && (
              <LoadingButton
                type="submit"
                variant="contained"
                loading={emailFallbackStatus === 'verifying'}
                disabled={emailFallbackCode.length !== 6}
              >
                Verify code
              </LoadingButton>
            )}

            <LoadingButton
              type="button"
              variant={emailFallbackMaskedEmail ? 'text' : 'outlined'}
              size={emailFallbackMaskedEmail ? 'small' : 'medium'}
              loading={emailFallbackStatus === 'sending'}
              disabled={resendCooldown > 0 || emailFallbackStatus === 'verifying'}
              onClick={handleRequestEmailFallback}
            >
              {resendCooldown > 0
                ? `Resend code in ${resendCooldown}s`
                : emailFallbackMaskedEmail
                  ? 'Resend code'
                  : 'Email me a code'}
            </LoadingButton>
          </Stack>
        )}

        <Button fullWidth variant="outlined" onClick={handleCancelDuo}>
          Cancel
        </Button>
      </Stack>
    );
  }

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmitLogin)}>
      <Stack spacing={2}>
        {!!errors.afterSubmit && (
          <Alert severity="error">{errors.afterSubmit.message}</Alert>
        )}
        <RHFTextField
          name="email"
          label="Email address"
          inputProps={{
            autoComplete: 'username',
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          inputProps={{
            autoComplete: 'current-password',
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleToggleShowPassword} edge="end">
                  <Iconify
                    icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <Link
          component={RouterLink}
          variant="subtitle2"
          to={authPath('reset')}
          sx={{ alignSelf: 'flex-end' }}
        >
          Forgot password?
        </Link>

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
        >
          Login
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
}
